import {
  type AuthenticateOptions,
  type AuthProvider,
} from '@/features/iam/auth/AuthStore'
import { decodeTokenInfo, type TokenInfo } from '../token-info'
import env from '@/infra/env'
import { Auth0Client } from '@auth0/auth0-spa-js'
import { isAbortError } from '@/utils/abort-util'
import { type GetExternalUrlDelegate } from '@/features/shell/platform/types'

/**
 * Provides authentication through Auth0.
 */
export class Auth0Provider implements AuthProvider {
  private readonly _auth0: Auth0Client

  constructor(
    private readonly _getExternalUrlDelegate: GetExternalUrlDelegate,
  ) {
    this._auth0 = new Auth0Client({
      domain: env.AUTH0_DOMAIN,
      clientId: env.AUTH0_CLIENT_ID,
      cacheLocation: 'localstorage',
      useRefreshTokens: true,
      useRefreshTokensFallback: true,
      authorizationParams: {
        audience: env.AUTH0_AUDIENCE_URL,
        redirect_uri: window.location.origin + '/auth/callback',
        scope: 'openid profile email offline_access',
      },
    })
  }

  async fetchTokenInfo(signal?: AbortSignal): Promise<TokenInfo | null> {
    // Client caches tokens in localstorage and only reaches out to
    // auth0 when token is about to expire
    try {
      const isAuthenticated = await this._auth0.isAuthenticated()
      signal?.throwIfAborted()
      if (!isAuthenticated) {
        return null
      }

      const token = await this._auth0.getTokenSilently()
      return decodeTokenInfo(token)
    } catch (err: unknown) {
      if (isErrorIndicatingLackOfAuth(err)) {
        return null
      }

      if (isAbortError(err)) {
        return null
      }

      console.error('Get token silently error:', err)
      throw err
    }
  }

  async callback() {
    const hashParam = new URLSearchParams(window.location.hash.substring(1))

    // this handles the authentication when using auth0 impersonation legacy api
    if (hashParam.has('id_token')) {
      await this._auth0.getTokenSilently()
      return null
    }

    const response = await this._auth0.handleRedirectCallback<AppState>()
    return response.appState?.returnUrl ?? null
  }

  redirectToLogin(opts: AuthenticateOptions): Promise<void> {
    return this._authenticate(opts, 'login')
  }

  redirectToSignup(opts: AuthenticateOptions): Promise<void> {
    return this._authenticate(opts, 'signup')
  }

  logout(): Promise<void> {
    return this._auth0.logout({
      logoutParams: {
        returnTo: window.location.origin + '/logout',
      },
    })
  }

  /**
   * Authenticates the current user by redirecting to the auth provider.
   */
  private _authenticate(
    opts: AuthenticateOptions,
    screenHint: 'login' | 'signup',
  ) {
    const appState: AppState = {
      returnUrl: opts.returnUrl,
    }
    return this._auth0.loginWithRedirect({
      appState,
      authorizationParams: {
        screen_hint: screenHint,
      },
      openUrl: (url) => {
        const handle = this._getExternalUrlDelegate().createHandle({
          sameWindow: true,
        })
        handle.open(url)
      },
    })
  }
}

/**
 * State associated with an authentication operation.
 */
interface AppState {
  /** @see {AuthenticateOptions} */
  returnUrl?: string | null
}

/**
 * Checks whether the error indicates that we need to (re)authenticate.
 *
 * @param err
 */
function isErrorIndicatingLackOfAuth(err: unknown) {
  // @ts-ignore
  return (
    err instanceof Error &&
    'error' in err &&
    typeof err.error === 'string' &&
    (err.error === 'login_required' ||
      err.error === 'missing_refresh_token' ||
      err.error === 'invalid_grant')
  )
}
